import { t } from '@lingui/core/macro';
import { type CarsWithSalesItemsForBookingResponseRecord, SalesItemBookingCategoryType } from '@navio/nsdk';
import groupBy from 'lodash/groupBy';
import { SALES_ITEM_BOOKING_FLOW_TYPE } from 'noddi-types';
import { NoddiButton, NoddiInfoBanner, NoddiLink } from 'noddi-ui-web';
import { compareLicensePlates, customerRoutes } from 'noddi-util';
import {
  getUniqueSalesItems,
  numberOfSelectedItemsFleets,
  useBookingActions,
  useBookingCars,
  useBookingSelectedSalesItemsCars
} from 'noddi-web';

import { CarIdentifierWithIcon } from '@components/BookingFlow/Common/CarIdentifierWithIcon';
import { Trans } from '@lingui/react/macro';
import { useCurrentBrand } from '../../../../stores/BrandStore/brandStore';
import { getCarName } from '../../../../utils/carName';
import { SalesItemMultipleCars } from './SalesItemMultipleCars';
import { ServiceCategoryFleetCollapseCard } from './ServiceCategoryFleetCollapseCard';

export const SalesItemsMultipleCarsSelector = ({
  availableSalesItems,
  salesItemType
}: {
  availableSalesItems: CarsWithSalesItemsForBookingResponseRecord;
  salesItemType: SALES_ITEM_BOOKING_FLOW_TYPE;
}) => {
  const selectedSalesItemsCars = useBookingSelectedSalesItemsCars();
  const { brandEmail } = useCurrentBrand();

  const uniqueSalesItems = getUniqueSalesItems(availableSalesItems);
  const { removeCar } = useBookingActions();
  const bookingCars = useBookingCars();

  const sortedSalesItems = [...uniqueSalesItems].sort((a, b) => a.uiSortOrder - b.uiSortOrder);

  const bookingCategories = groupBy(sortedSalesItems, 'bookingCategoryType');
  const numberOfCategories = Object.keys(bookingCategories).length;

  const onlyOneCategory = numberOfCategories === 1;

  const mountedAsExpanded = salesItemType === SALES_ITEM_BOOKING_FLOW_TYPE.ADDON || onlyOneCategory;

  // Nothing came back for any car. Addons are optional, so that is just an empty step; a primary
  // step with nothing bookable needs a way out instead.
  if (!numberOfCategories) {
    return salesItemType === SALES_ITEM_BOOKING_FLOW_TYPE.ADDON ? (
      <NoddiInfoBanner texts={[{ value: t`We don't have any addons available for the services you've selected` }]} />
    ) : (
      <NoddiInfoBanner
        variant='info'
        title={t`No services available`}
        texts={[{ value: t`We couldn't find any services you can book for the selected cars at this address.` }]}
        actionNode={
          <>
            <NoddiLink to={customerRoutes.newBookingAddress.getPath()} size='small' variant='outline'>
              <Trans>Change address</Trans>
            </NoddiLink>
            <NoddiLink to={customerRoutes.newBookingCars.getPath()} size='small' variant='outline'>
              <Trans>Change cars</Trans>
            </NoddiLink>
          </>
        }
      />
    );
  }
  // find out if salesItems is empty array for cars - and if so return that
  const carsWithNoAvailableSalesItems = availableSalesItems.cars.filter((car) => car.salesItems.length === 0);

  return (
    <>
      {carsWithNoAvailableSalesItems.map((salesItemCar) => {
        const car = bookingCars.find((car) => compareLicensePlates(car.licensePlate, salesItemCar.licensePlate));
        const carName = getCarName({ car: car ?? salesItemCar, showLicensePlate: true });

        if (!car) {
          return null;
        }

        if (salesItemType === SALES_ITEM_BOOKING_FLOW_TYPE.PRIMARY) {
          return (
            <div key={salesItemCar.licensePlate.number} className='flex flex-col gap-5'>
              <NoddiInfoBanner
                variant='warning'
                title={t`We do not provide tire hotel services in this area`}
                texts={[
                  {
                    value: t`${carName} has an active tire hotel contract, but we do not provide tire hotel services in this area - please contact us at ${brandEmail} if you need help with this`
                  }
                ]}
              />
              <div className='flex justify-end'>
                <NoddiButton variant='outline' size='small' onClick={() => removeCar(car)}>
                  {t`Remove car from booking`}
                </NoddiButton>
              </div>
            </div>
          );
        }

        return null;
      })}

      <div className='flex flex-col gap-4'>
        {onlyOneCategory
          ? // When there's only one category and it's not addon, render sales items directly without collapsible wrapper
            sortedSalesItems?.map((item) => (
              <SalesItemMultipleCars key={item.name} type={salesItemType} salesItem={item} />
            ))
          : // When there are multiple categories use collapsible cards
            Object.entries(bookingCategories).map(([category, salesItems]) => {
              const numberOfSelectedSalesItems = numberOfSelectedItemsFleets({
                salesItemsInCategory: salesItems,
                selectedSalesItems: selectedSalesItemsCars
              });

              return (
                <ServiceCategoryFleetCollapseCard
                  key={category}
                  bookingCategoryType={category as SalesItemBookingCategoryType}
                  salesItems={salesItems}
                  numberOfSelectedSalesItems={numberOfSelectedSalesItems}
                  salesItemType={salesItemType}
                  mountAsExpanded={mountedAsExpanded}
                />
              );
            })}
      </div>

      {carsWithNoAvailableSalesItems.map((salesItemCar) => {
        const car = bookingCars.find((car) => compareLicensePlates(car.licensePlate, salesItemCar.licensePlate));

        if (!car) {
          return null;
        }

        if (salesItemType === SALES_ITEM_BOOKING_FLOW_TYPE.ADDON) {
          return (
            <div key={salesItemCar.licensePlate.number} className='flex flex-col gap-5'>
              <CarIdentifierWithIcon hideTireHotelInfo car={car} bookingCategories={Object.keys(bookingCategories)} />
              <NoddiInfoBanner
                texts={[
                  {
                    value: t`Unfortunately, we don't have offer any addons for the services you've selected on this car`
                  }
                ]}
              />
            </div>
          );
        }

        return null;
      })}
    </>
  );
};
